import React, { useState, useEffect } from 'react';
import { X, BookOpen, HelpCircle, FileText, Video, MessageSquare, Search, ChevronRight, ChevronDown } from 'lucide-react';
import { updatePageSEO } from '../utils/seo';

const ResourcesPage = ({ onClose, onNavigateToSupport }) => {
  const [activeSection, setActiveSection] = useState('help'); // 'help', 'learn', 'faq'
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [expandedTopic, setExpandedTopic] = useState(null);

  // Update SEO tags when component mounts
  useEffect(() => {
    updatePageSEO({
      title: 'Resources & Help Center - PlanLens.ai | Financial Planning Guides',
      description: 'Get help with PlanLens.ai. Access comprehensive guides, tutorials, FAQs, and support for portfolio tracking, retirement planning, and wealth management features.',
      canonical: 'https://planlens.ai/resources'
    });
  }, []);

  const helpArticles = [
    {
      category: "Getting Started",
      articles: [
        {
          title: "Creating Your Account",
          description: "Learn how to sign up and set up your profile",
          content: `Creating your PlanLens account is quick and easy:

1. **Click "Get Started"** on the landing page
2. **Select Your Country** - Choose US or UK to get country-specific features like appropriate account types (401k/IRA for US, ISA/SIPP for UK) and currency formatting
3. **Sign Up** - Enter your email and create a secure password (minimum 8 characters)
4. **Accept Terms** - Review and accept our terms of service and privacy policy
5. **Verify Email** - Check your inbox for a verification email and click the link

Once verified, you'll be taken directly to your dashboard where you can start adding investments, setting goals, and tracking your net worth.

**Pro Tip:** Use a strong, unique password and consider enabling two-factor authentication in your profile settings for extra security.`
        },
        {
          title: "Choosing Your Country (US/UK)",
          description: "Understanding country-specific features",
          content: `PlanLens is optimized for both US and UK users with country-specific features:

**US Features:**
- Currency: US Dollar ($)
- Account Types: 401(k), Traditional IRA, Roth IRA, Brokerage, 529 Plans
- Tax considerations optimized for US tax law
- Retirement age defaults and contribution limits based on IRS guidelines

**UK Features:**
- Currency: British Pound (£)
- Account Types: ISA, SIPP, Pension, General Investment Account
- Tax considerations optimized for UK tax law (CGT, ISA allowances)
- Retirement age defaults and contribution limits based on HMRC guidelines

**Important:** Your country selection is set during signup and cannot be changed later without contacting support, so choose carefully!

**Can I use PlanLens from other countries?** While you can access PlanLens from anywhere, the tool is currently optimized for US and UK residents with appropriate account types and tax rules for those countries.`
        },
        {
          title: "Dashboard Overview",
          description: "Navigate your financial dashboard",
          content: `Your PlanLens dashboard is your financial command center. Here's what you'll find:

**Portfolio Overview Card:**
- Total portfolio value across all accounts
- Total number of holdings and accounts
- Quick visual summary of your wealth

**Asset Allocation Chart:**
- Visual pie chart showing your investment distribution
- Breakdown by asset class (Stocks, Bonds, Real Estate, Crypto, Cash, Commodities)
- Interactive legend with percentages and values
- Click on segments for detailed information

**Recent Activity:**
- Latest investments added
- Recent changes to your portfolio
- Goal progress updates

**Quick Actions:**
- Add Investment button for quick entry
- Import CSV for bulk uploads
- Access to all major features via left sidebar navigation

**Navigation:**
- **Wealth:** Net Worth, Assets, Liabilities
- **Investments:** Accounts, Portfolio Analysis
- **Planning:** Financial Goals, Retirement Planning
- **Insights:** AI-powered analysis (Premium)
- **More:** Profile, Help, Support

The dashboard automatically updates as you add investments, change allocations, or update your goals. All data syncs in real-time across all your devices.`
        },
        {
          title: "Dark Mode & Settings",
          description: "Customize your experience",
          content: `Customize PlanLens to match your preferences:

**Dark Mode:**
- Click the moon/sun icon in the top-right header
- Switches between light and dark themes
- Reduces eye strain in low-light environments
- Preference is saved automatically and syncs across devices

**Profile Settings:**
Access via the More menu > Profile:
- **Personal Information:** Update name, email, profile photo
- **Country & Currency:** View your selected country (contact support to change)
- **Account Management:** Change password, enable 2FA
- **Subscription:** View plan, upgrade to Premium, manage billing
- **Data Export:** Download your data as CSV
- **Account Deletion:** Permanently delete your account

**Privacy Settings:**
- Review terms and privacy policy anytime
- Manage data sharing preferences
- See what data we collect and why
- Export all your data

**Notification Preferences:**
- Email notifications for goal milestones
- Alerts when you're off track on goals
- Premium feature announcements
- Security alerts (recommended to keep enabled)

All settings changes are saved automatically and sync across all your devices instantly.`
        }
      ]
    },
    {
      category: "Portfolio Management",
      articles: [
        {
          title: "Adding Investments",
          description: "Track stocks, bonds, and other assets",
          content: `Add investments to track your entire portfolio in one place:

**Adding Individual Investments:**
1. Navigate to Wealth > Assets in the left sidebar
2. Click the "+ Add Investment" button
3. Fill in the details:
   - **Name:** Company or fund name (e.g., "Apple Inc.")
   - **Ticker Symbol:** Stock ticker (e.g., "AAPL") - optional but recommended
   - **Amount Invested:** Total value in your chosen currency
   - **Expected Return:** Annual expected return percentage (e.g., 7% for stocks)
   - **Risk Level:** Low, Medium, or High
   - **Category:** Stocks, Bonds, Real Estate, Commodities, or Cash
   - **Account:** Link to a specific account (401k, IRA, ISA, etc.)
   - **Purchase Date:** When you acquired the investment
4. Click "Add Investment" to save

**Investment Categories:**
- **Stocks:** Individual company shares or equity ETFs
- **Bonds:** Government bonds, corporate bonds, bond funds
- **Real Estate:** REITs, property investments
- **Commodities:** Gold, silver, oil, commodity ETFs
- **Cash:** Money market funds, savings accounts, CDs

**Best Practices:**
- Be consistent with naming (e.g., always use full company names)
- Use ticker symbols when available for clarity
- Link investments to specific accounts for better organization
- Update values regularly for accurate portfolio tracking
- Use realistic expected returns based on historical averages`
        },
        {
          title: "Managing Accounts",
          description: "Set up 401(k), IRA, ISA, and other accounts",
          content: `Organize your investments by accounts for better tracking:

**Creating Accounts:**
1. Go to Investments > Accounts in the left sidebar
2. Click "+ Add Account Type"
3. Enter account details:
   - **Account Name:** Descriptive name (e.g., "Vanguard 401(k)")
   - **Account Type:** Select from country-specific options
   - **Institution:** Bank or broker name (optional)
   - **Description:** Additional notes

**US Account Types:**
- **401(k):** Employer-sponsored retirement account
- **Traditional IRA:** Pre-tax retirement account
- **Roth IRA:** After-tax retirement account
- **Brokerage:** Taxable investment account
- **529 Plan:** College savings account

**UK Account Types:**
- **ISA:** Tax-free Individual Savings Account
- **SIPP:** Self-Invested Personal Pension
- **Pension:** Workplace or personal pension
- **General Investment Account:** Taxable investment account

**Account Benefits:**
- Group investments by account for clearer organization
- See total value per account
- Track account-specific performance
- Understand tax implications per account type
- Export account-specific reports

**Managing Existing Accounts:**
- Edit account names or details anytime
- View all investments within an account
- See account-level allocation and performance
- Delete accounts (will unlink investments, not delete them)

Link new investments to accounts when adding them to maintain organization.`
        },
        {
          title: "Importing Data via CSV",
          description: "Bulk import your existing investments",
          content: `Save time by importing multiple investments at once:

**Step-by-Step Import Process:**
1. Go to Wealth > Assets
2. Click "Import CSV" button
3. Download the template file
4. Fill in your investment data in the template
5. Upload the completed CSV file
6. Review the preview
7. Click "Import" to add all investments

**CSV Template Format:**
The template includes these columns:
- **name:** Investment name (required)
- **ticker:** Ticker symbol (optional)
- **amount:** Dollar/pound value (required, numbers only)
- **return:** Expected annual return % (required, e.g., "7" for 7%)
- **risk:** Low, Medium, or High (required)
- **category:** Stocks, Bonds, Real Estate, etc. (required)
- **accountId:** Leave blank for manual account linking after import
- **purchaseDate:** YYYY-MM-DD format (optional)

**Example Rows:**
Apple Stock,AAPL,10000,10,Medium,Stocks,,2024-01-15
Vanguard Total Bond,BND,5000,4,Low,Bonds,,2024-01-15
Real Estate Fund,VNQ,3000,8,Medium,Real Estate,,2024-02-01

**Tips for Successful Import:**
- Use the exact template format provided
- Don't add extra columns or change column names
- Use numbers only for amount and return (no $ or % symbols)
- Match category names exactly: Stocks, Bonds, Real Estate, Commodities, Cash
- Keep data clean with no special characters
- Review the preview carefully before importing

**After Import:**
- Link investments to accounts manually if needed
- Review all imported data for accuracy
- Edit any investments that need correction
- Your portfolio allocation will update automatically

**Troubleshooting:**
- If import fails, check for formatting errors
- Ensure all required fields are filled
- Remove any blank rows
- Use plain text, not formulas`
        },
        {
          title: "Viewing Portfolio Allocation",
          description: "Understand your asset distribution",
          content: `Visualize and understand how your investments are distributed:

**Dashboard Allocation Chart:**
Your dashboard shows a large, interactive pie chart displaying your asset allocation:

**Asset Classes Tracked:**
- **Stocks (Blue):** Equities, stock funds, individual shares
- **Bonds (Green):** Fixed income, bond funds, treasuries
- **Real Estate (Orange):** REITs, property investments
- **Commodities (Brown):** Gold, silver, oil, commodity funds
- **Cash (Purple):** Money market, savings, CDs

**Chart Features:**
- Hover over segments to see exact values and percentages
- Color-coded segments for quick identification
- Legend shows all categories with values
- Updates in real-time as you add/edit investments

**Different Views:**
You can view your portfolio in three ways:

1. **All Investments:** Complete portfolio overview
2. **By Account:** See allocation within specific accounts (401k, IRA, ISA, etc.)
3. **By Category:** Focus on specific asset classes

Switch views using the view selector above the portfolio list.

**Allocation Insights:**
- **Well-Diversified:** Multiple asset classes, no single category >60%
- **Stock-Heavy:** >70% in stocks (higher risk, higher potential return)
- **Conservative:** >50% in bonds/cash (lower risk, lower return)
- **Balanced:** Mix of stocks (40-60%), bonds (30-40%), other assets

**Best Practices:**
- Review allocation monthly
- Rebalance when categories drift >5% from targets
- Age-appropriate allocation: stocks% ≈ 110 - your age
- Consider target-date funds for automatic rebalancing

Use the Premium Gemini AI Analysis feature for personalized allocation recommendations!`
        }
      ]
    },
    {
      category: "Net Worth & Goals",
      articles: [
        {
          title: "Understanding Net Worth Dashboard",
          description: "See your complete financial picture",
          content: `The Net Worth Dashboard gives you a complete view of your financial health:

**What is Net Worth?**
Net Worth = Total Assets - Total Liabilities

**Dashboard Components:**

1. **Net Worth Summary Card:**
   - Your total net worth in large, bold numbers
   - Month-over-month change indicator
   - Financial health score (color-coded)

2. **Assets Breakdown:**
   - Investment accounts value
   - Cash and savings
   - Real estate and other assets
   - Visual chart showing asset composition

3. **Liabilities Breakdown:**
   - Mortgages and home loans
   - Student loans
   - Auto loans
   - Credit card debt
   - Personal loans
   - Visual chart showing debt composition

4. **Net Worth Trend:**
   - Historical chart showing net worth over time
   - Track your progress month by month
   - See the impact of your financial decisions

**Financial Health Indicators:**
- **Excellent (Green):** Net worth growing consistently, low debt-to-asset ratio
- **Good (Blue):** Positive net worth, manageable debt levels
- **Fair (Yellow):** Breaking even, high debt-to-asset ratio
- **Needs Attention (Red):** Negative net worth, debt exceeds assets

**How to Improve Your Net Worth:**
1. Increase assets by investing regularly
2. Reduce high-interest debt aggressively
3. Build emergency fund (3-6 months expenses)
4. Increase income through career growth or side hustles
5. Track spending and cut unnecessary expenses
6. Take advantage of employer retirement matches

**Best Practices:**
- Update monthly for accurate tracking
- Include ALL assets and liabilities for complete picture
- Don't forget hidden assets (retirement accounts, HSAs, etc.)
- Track home value realistically (use recent comparable sales)
- Include car values if owned outright

Premium users get AI-powered insights on how to optimize their net worth growth!`
        },
        {
          title: "Adding Liabilities",
          description: "Track mortgages, loans, and credit cards",
          content: `Track all your debts to get an accurate net worth calculation:

**How to Add Liabilities:**
1. Navigate to Wealth > Liabilities in the left sidebar
2. Click "+ Add Liability"
3. Fill in the details:
   - **Name:** Descriptive name (e.g., "Chase Credit Card", "Home Mortgage")
   - **Type:** Select category (Mortgage, Credit Card, Student Loan, Auto Loan, Personal Loan, Other)
   - **Balance:** Current amount owed
   - **Interest Rate:** Annual percentage rate (APR)
   - **Monthly Payment:** Your regular payment amount
   - **Lender:** Financial institution name
   - **Due Date:** Payment due date each month
4. Click "Add Liability" to save

**Liability Types Explained:**

**Mortgages:**
- Largest debt for most people
- Typically 15-30 year terms
- Usually lowest interest rates due to collateral
- Include principal balance only, not home value

**Credit Cards:**
- Revolving debt with variable interest
- Usually highest interest rates (15-25% APR)
- Track each card separately
- Pay off in full monthly to avoid interest

**Student Loans:**
- Education debt with varying terms
- May have deferred payments or income-based repayment
- Federal loans typically 4-7% interest
- Private loans can be higher

**Auto Loans:**
- Secured by vehicle
- Typically 3-7 year terms
- Interest rates vary based on credit score
- Track alongside vehicle value (depreciating asset)

**Personal Loans:**
- Unsecured debt for various purposes
- Fixed terms and payments
- Interest rates vary widely (5-36%)
- Include medical debt, home improvements, etc.

**Debt Management Tips:**
- Pay off highest interest debt first (avalanche method)
- OR pay off smallest balances first for psychological wins (snowball method)
- Always pay minimums on all debts
- Consider refinancing high-interest debt
- Avoid taking on new debt while paying off existing
- Build emergency fund to avoid new debt

**Warning Signs:**
- Debt-to-income ratio >43%
- Only making minimum payments
- Using credit cards for necessities
- Borrowing to pay off other debt

Use the Net Worth Dashboard to track how your debt payoff affects your overall financial health!`
        },
        {
          title: "Setting Financial Goals",
          description: "Create and track your financial objectives",
          content: `Set and achieve your financial goals with PlanLens:

**Creating Goals:**
1. Go to Planning > Financial Goals
2. Click "+ Add Goal"
3. Enter goal details:
   - **Goal Name:** What you're saving for (e.g., "Emergency Fund", "House Down Payment")
   - **Target Amount:** How much you need
   - **Target Date:** When you want to achieve it
   - **Current Progress:** Amount already saved (optional)
   - **Monthly Contribution:** How much you'll save per month
   - **Priority:** High, Medium, or Low
4. Click "Create Goal"

**Goal Types & Examples:**

**Short-Term Goals (< 1 year):**
- Emergency fund ($3,000-$10,000)
- Vacation ($2,000-$5,000)
- Holiday shopping ($1,000-$3,000)
- New phone or laptop ($500-$2,000)

**Medium-Term Goals (1-5 years):**
- House down payment ($20,000-$100,000)
- New car ($10,000-$40,000)
- Wedding ($15,000-$50,000)
- Home renovation ($10,000-$50,000)

**Long-Term Goals (5+ years):**
- Retirement (varies widely)
- Children's college fund ($50,000-$200,000 per child)
- Early retirement/FIRE
- Starting a business ($50,000-$200,000)

**How PlanLens Helps:**
- Calculates required monthly savings
- Shows if you're on track with visual indicators
- Accounts for compound interest on invested goals
- Warns you if timeline is unrealistic
- Tracks actual vs. expected progress

**Goal Setting Best Practices:**
1. **Use SMART Goals:**
   - Specific: "Save $20,000 for house down payment"
   - Measurable: Track progress in dollars
   - Achievable: Based on your actual income
   - Relevant: Aligned with your values
   - Time-bound: Set realistic deadlines

2. **Prioritize Correctly:**
   - Emergency fund FIRST (3-6 months expenses)
   - Pay off high-interest debt
   - Retirement savings (especially with employer match)
   - Other goals in priority order

3. **Be Realistic:**
   - Don't set impossible timelines
   - Account for irregular expenses
   - Build in buffer for unexpected costs
   - Adjust as circumstances change

4. **Review Regularly:**
   - Check progress monthly
   - Adjust contributions as needed
   - Celebrate milestones!
   - Update timelines if necessary

**Advanced Features:**
- Link goals to specific accounts
- Set automatic contribution reminders
- Factor in expected investment returns
- Track multiple goals simultaneously
- Premium: Get AI recommendations for goal optimization

Stay motivated by tracking your progress and celebrating when you hit milestones!`
        },
        {
          title: "Goal Progress Tracking",
          description: "Monitor your progress toward goals",
          content: `Track your financial goals and stay on course:

**Progress Indicators:**

**Visual Progress Bars:**
- Fill up as you save toward your goal
- Color-coded status:
  - **Green:** On track or ahead of schedule
  - **Yellow:** Slightly behind but achievable
  - **Red:** Significantly off track
- Percentage complete displayed
- Amount saved vs. target amount

**Timeline Tracking:**
- Days/months remaining
- Target completion date
- Projected completion date (based on current savings rate)
- Warning if projected date > target date

**Contribution Tracking:**
- Required monthly savings amount
- Actual contributions made
- Shortfall or surplus
- Automatic calculation adjustments

**Status Messages:**
- **"On Track!"** - Current pace will meet deadline
- **"Ahead of Schedule!"** - Exceeding target pace
- **"Needs Attention"** - Falling behind
- **"At Risk"** - Significantly off pace

**Updating Progress:**
1. Navigate to Planning > Financial Goals
2. Find your goal in the list
3. Click "Update Progress" or edit icon
4. Enter new saved amount or monthly contribution
5. System recalculates timeline automatically

**What to Do When Off Track:**

**If Falling Behind:**
1. Increase monthly contributions
2. Extend target deadline (if flexible)
3. Reduce target amount (if appropriate)
4. Cut expenses to free up more savings
5. Look for one-time income boosts (bonuses, side gigs)

**If Ahead of Schedule:**
1. Maintain current pace and finish early
2. Increase goal target if needed
3. Redirect surplus to other goals
4. Start a new goal
5. Increase investment allocation for growth

**Smart Tracking Tips:**
- Update monthly after payday
- Be honest about actual savings
- Account for irregular contributions (bonuses, tax refunds)
- Don't forget to include investment gains
- Adjust for inflation on long-term goals

**Milestone Celebrations:**
Set sub-milestones to stay motivated:
- 25% complete - "Quarter of the way!"
- 50% complete - "Halfway there!"
- 75% complete - "In the home stretch!"
- 100% complete - "Goal achieved! 🎉"

**Multiple Goal Management:**
- Prioritize goals by importance
- Allocate funds based on priority
- Some parallel goals okay (retirement + house saving)
- Don't spread yourself too thin
- Focus on 2-3 active goals maximum

**Accountability Features:**
- Regular email reminders (if enabled)
- Dashboard warnings for off-track goals
- Progress notifications
- Achievement badges (Premium)

**Goal Adjustments:**
Life changes, and goals should too:
- Got a raise? Increase contributions
- Unexpected expense? Adjust timeline
- Priorities shifted? Pause or delete goals
- Goal achieved? Set a new one!

PlanLens makes it easy to track multiple goals simultaneously and stay motivated on your financial journey!`
        }
      ]
    },
    {
      category: "Retirement Planning",
      articles: [
        {
          title: "Setting Retirement Goals",
          description: "Plan for your future retirement",
          content: `Plan for a comfortable retirement with PlanLens:

**Retirement Planning Basics:**
1. Navigate to Planning > Retirement
2. Enter your information:
   - **Current Age:** Your age today
   - **Retirement Age:** When you plan to retire (typically 65-67)
   - **Current Savings:** Total retirement savings now
   - **Monthly Contribution:** How much you save monthly
   - **Desired Annual Income:** What you want to live on in retirement
   - **Expected Return:** Investment return rate (typically 6-8%)
3. Click "Calculate" to see projections

**How Much Do You Need?**
Common retirement rules of thumb:

**The 25x Rule:**
- Need 25 times your annual expenses
- Allows 4% safe withdrawal rate
- Example: $50,000/year needs $1.25 million

**The 80% Rule:**
- Replace 80% of pre-retirement income
- Account for reduced expenses
- No more commuting, work clothes, etc.

**The 4% Rule:**
- Withdraw 4% of portfolio annually
- Adjust for inflation each year
- Historically sustainable for 30+ years

**Retirement Age Considerations:**

**Early Retirement (55-62):**
- Need larger nest egg (longer retirement)
- Can't access Social Security yet
- Medicare not available until 65
- May face early withdrawal penalties
- Healthcare costs critical concern

**Normal Retirement (65-67):**
- Full Social Security benefits available
- Medicare eligibility
- Standard planning assumptions
- Balanced approach

**Late Retirement (68+):**
- Increased Social Security benefits
- More time to save
- Shorter retirement period needed
- Potentially better health
- May enjoy work or need income

**Contribution Strategies:**
Covered in separate article, but key points:
- Start early (compound interest!)
- Get employer match (free money!)
- Max out tax-advantaged accounts
- Increase contributions with raises
- Catch-up contributions at 50+

**Country-Specific Considerations:**

**United States:**
- Social Security (check SSA.gov for estimate)
- Medicare at 65
- 401(k) and IRA accounts
- Required Minimum Distributions at 73
- Consider state taxes in retirement

**United Kingdom:**
- State Pension (check gov.uk for amount)
- NHS healthcare continues
- SIPP and workplace pensions
- Personal Allowance (tax-free amount)
- 25% tax-free lump sum option

**Common Retirement Mistakes:**
- Starting too late
- Underestimating expenses
- Forgetting about inflation
- Ignoring healthcare costs
- Taking Social Security too early
- Too conservative with investments
- Not having withdrawal strategy

**PlanLens Enhanced Retirement Features:**
- Interactive retirement calculator with real-time projections
- Advanced scenario analysis (optimistic, expected, pessimistic)
- Visual timeline charts and graphs
- Contribution and withdrawal strategy optimization
- Social Security/State Pension integration
- Inflation-adjusted projections
- Gap analysis with shortfall warnings
- AI-powered retirement recommendations (Premium)
- Monte Carlo simulations for success probability (Premium)
- Multiple scenario comparisons (Premium)

**Action Steps:**
1. Calculate retirement needs (use our calculator)
2. Determine monthly savings required
3. Set up automatic contributions
4. Review and adjust annually
5. Increase savings with raises
6. Stay disciplined and consistent

Start planning today - your future self will thank you!`
        },
        {
          title: "Contribution Strategies",
          description: "Maximize your retirement savings",
          content: `Optimize your retirement contributions for maximum growth:

**Priority Order for Contributions:**

**1. Employer Match (Top Priority)**
- Contribute enough to get full match
- Typically 3-6% of salary
- Instant 50-100% return on investment
- This is FREE MONEY - never leave it on the table

**2. High-Interest Debt Payoff**
- Pay off credit cards (15-25% interest)
- Pay off personal loans (10-15% interest)
- Debt payoff = guaranteed return

**3. Max Out Roth IRA/Roth Option**
- Tax-free growth and withdrawals
- Best for younger workers (lower tax bracket now)
- 2024 limit: $7,000 ($8,000 if 50+)

**4. Max Out 401(k)/SIPP**
- Increase to annual maximum
- 2024 US limit: $23,000 ($30,500 if 50+)
- UK: £60,000 annual allowance

**5. HSA (US Only)**
- Triple tax advantage
- Use for medical expenses in retirement
- 2024 limit: $4,150 single, $8,300 family

**6. Taxable Brokerage Account**
- No limits or restrictions
- More flexibility
- Less tax-advantaged

**Contribution Amount Guidelines:**

**By Age:**
- **20s:** 10-15% of gross income
- **30s:** 15-20% of gross income
- **40s:** 20-25% of gross income
- **50s:** 25-30% of gross income
- **60s:** 30%+ if behind

**By Retirement Goals:**
- Comfortable retirement: 15-20%
- Early retirement: 30-50%
- Maintain lifestyle: 20-25%
- Downsize in retirement: 10-15%

**Smart Contribution Strategies:**

**1. Automate Everything**
- Set up automatic transfers
- "Pay yourself first"
- Don't see it, don't spend it
- Treat it like a bill

**2. Increase with Raises**
- Got a 3% raise? Save 2%, spend 1%
- Lifestyle creep is real
- Won't miss money you never had
- Accelerates savings dramatically

**3. Save Windfalls**
- Tax refunds → retirement
- Bonuses → retirement (at least 50%)
- Inheritance → retirement
- Side income → retirement

**4. Catch-Up Contributions (Age 50+)**
- Additional $7,500 to 401(k)
- Additional $1,000 to IRA
- Take advantage if you can
- Make up for lost time

**Tax Optimization:**

**Traditional vs Roth:**

**Traditional (Pre-Tax):**
- Lowers current tax bill
- Best if in high bracket now
- Pay taxes in retirement
- Required Minimum Distributions

**Roth (After-Tax):**
- No current tax benefit
- Tax-free in retirement
- Best if in low bracket now
- No RMDs

**Tax Diversification:**
Have both for flexibility:
- Some traditional (tax-deferred)
- Some Roth (tax-free)
- Some taxable (flexibility)
- Optimize withdrawals in retirement

**Contribution Timing:**

**Dollar-Cost Averaging:**
- Invest same amount regularly
- Automatic through payroll
- Buy more when prices low
- Buy less when prices high
- Reduces market timing risk

**Lump Sum Investing:**
- Got a bonus or windfall?
- Research shows lump sum often wins
- But dollar-cost averaging feels safer
- Choose what lets you sleep at night

**Common Mistakes to Avoid:**
- Not getting full employer match
- Stopping during market downturns
- Cashing out when changing jobs
- Borrowing from 401(k)
- Not increasing with raises
- Waiting to "catch up later"
- Being too conservative (or too aggressive)

**Rebalancing Strategy:**
- Review allocation annually
- Rebalance when 5%+ off target
- Use new contributions to rebalance
- Avoid excessive trading

**Track Your Progress:**
- Calculate savings rate monthly
- Review total balance quarterly
- Project retirement income annually
- Adjust as needed
- Use PlanLens tracking tools

**Employer Benefits to Maximize:**
- 401(k) match
- Profit sharing
- Stock options (if applicable)
- HSA employer contributions
- Mega backdoor Roth (if available)

The best time to start was 20 years ago. The second best time is today!`
        },
        {
          title: "Retirement Projections",
          description: "Understand your retirement timeline",
          content: `Use PlanLens to project your retirement readiness:

**How Projections Work:**

**Inputs Used:**
- Current age and retirement age
- Current savings amount
- Monthly contribution amount
- Expected annual return (typically 6-8%)
- Desired annual retirement income
- Life expectancy (typically 85-95)

**Calculations:**
1. **Accumulation Phase:** How much you'll have at retirement
   - Current savings grow at expected return rate
   - Monthly contributions compound over time
   - Shows total at retirement age

2. **Distribution Phase:** Whether savings will last
   - Apply desired annual income withdrawals
   - Account for continued investment growth
   - Factor in inflation (typically 2-3%)
   - Show if money lasts through life expectancy

**Understanding Your Results:**

**Green - On Track:**
- Projected savings exceed needs
- Can retire on schedule
- May be able to retire earlier
- Or spend more in retirement

**Yellow - Close:**
- Projected savings nearly meet needs
- Small adjustments needed
- Extend retirement age 1-2 years
- Or increase contributions slightly

**Red - Shortfall:**
- Significant gap between savings and needs
- Need major changes
- Increase contributions substantially
- Or delay retirement
- Or reduce retirement spending

**Key Metrics Explained:**

**Savings at Retirement:**
Total accumulated wealth when you retire
- Your current savings grown
- Plus all future contributions grown
- This is your retirement "nest egg"

**Monthly Retirement Income:**
How much you can safely withdraw
- Typically 4% of total / 12 months
- Adjusts for inflation annually
- Should cover your expenses

**Years of Income:**
How long your money will last
- Should exceed life expectancy
- Account for longevity risk
- Better to have surplus than run out

**Required Monthly Savings:**
How much you need to contribute
- To meet your retirement goal
- Given your current situation
- Often eye-opening number!

**Scenario Analysis:**

**What-If Scenarios to Test:**

1. **Retire Earlier:**
   - Move retirement age to 62 instead of 67
   - See impact on required savings
   - Understand tradeoffs

2. **Increase Spending:**
   - What if you want $80k/year not $60k?
   - See how it affects timeline
   - Make informed choices

3. **Lower Returns:**
   - What if market returns only 5% not 8%?
   - Stress test your plan
   - Build in safety margin

4. **Increase Contributions:**
   - What if you save $500 more per month?
   - See dramatic impact of small increases
   - Motivate yourself to save more

**Adjusting Your Plan:**

**If You're Behind:**
- Increase monthly contributions
- Delay retirement 2-5 years
- Reduce retirement spending expectations
- Consider part-time work in retirement
- Maximize catch-up contributions (50+)

**If You're Ahead:**
- Maintain current pace
- Consider early retirement
- Increase lifestyle in retirement
- Leave larger legacy
- Help family members

**Important Assumptions:**

**Investment Returns:**
- Historical stock returns: ~10% annually
- Historical bond returns: ~5% annually
- Balanced portfolio: ~7% annually
- PlanLens default: 7%
- Be conservative in estimates

**Inflation:**
- Historical average: 2-3%
- Erodes purchasing power
- $50k today ≠ $50k in 30 years
- Plan accordingly

**Life Expectancy:**
- Increasing over time
- Plan to age 90-95 to be safe
- Longevity risk is real
- Better to overestimate

**Social Security/State Pension:**
- Not included in basic calculations
- Add as supplemental income
- US: Check SSA.gov for estimate
- UK: Check gov.uk for State Pension

**Advanced Features (Premium):**

**Monte Carlo Simulations:**
- Run 1,000+ scenarios
- See probability of success
- Account for market volatility
- More accurate than simple projections

**Historical Backtesting:**
- Test your plan against historical data
- See how it would have performed in past
- Includes recessions and bear markets
- Build confidence in plan

**Tips for Better Projections:**
- Be conservative with return estimates
- Include inflation
- Update annually as circumstances change
- Don't forget healthcare costs
- Plan for longer life than expected
- Build in buffer for unexpected expenses

**Regular Review:**
- Run projections annually
- After major life changes (marriage, kids, job change)
- When market has big moves
- As you get closer to retirement
- Adjust plan as needed

**Reality Check:**
If projections show you're far behind, don't panic:
1. Many people are in same boat
2. Small changes compound over time
3. Starting now is better than waiting
4. Consider working a few extra years
5. Partial retirement is an option
6. Social Security provides safety net

Use PlanLens projections to make informed decisions and stay on track for the retirement you deserve!`
        }
      ]
    },
    {
      category: "Premium Features",
      articles: [
        {
          title: "Running Gemini AI Analysis",
          description: "Get intelligent portfolio insights powered by Google Gemini",
          content: `Unlock AI-powered insights with Premium powered by Google's Gemini AI:

**What is Gemini AI Analysis?**
PlanLens Premium includes advanced AI analysis powered by Google's Gemini, providing comprehensive portfolio analysis with natural language understanding and personalized recommendations tailored to your unique financial situation.

**How to Run AI Analysis:**
1. Upgrade to Premium (if not already)
2. Navigate to Insights in the left sidebar
3. Click "Run AI Analysis"
4. Wait 10-30 seconds for analysis
5. Review comprehensive results

**What You Get:**

**1. Portfolio Health Score (0-100):**
- Overall portfolio quality rating
- Color-coded indicator
- Comparison to benchmarks
- Improvement suggestions

**2. Risk Assessment:**
- Volatility analysis
- Sharpe ratio calculation
- Risk-adjusted returns
- Diversification score
- Concentration risk warnings

**3. Asset Allocation Analysis:**
- Current vs. optimal allocation
- Rebalancing recommendations
- Age-appropriate suggestions
- Risk tolerance alignment

**4. Performance Metrics:**
- Expected annual returns
- Historical performance comparison
- Fee impact analysis
- Tax efficiency review

**5. Personalized Recommendations:**
- Specific actions to improve portfolio
- Priority-ordered suggestions
- Expected impact of each change
- Implementation guidance

**6. Scenario Projections:**
- Best case scenario
- Expected case scenario
- Worst case scenario
- Probability distributions

**7. Country-Specific Insights:**
- US: 401(k) vs IRA optimization, tax strategies
- UK: ISA vs SIPP optimization, tax wrappers

**AI Analysis Categories:**

**Diversification:**
- Are you over-concentrated?
- Missing asset classes?
- Correlation analysis
- Recommendations for better spread

**Risk Management:**
- Is your risk appropriate for age?
- Too aggressive or conservative?
- How to adjust risk level
- Emergency fund assessment

**Tax Optimization:**
- Are you using tax-advantaged accounts effectively?
- Tax-loss harvesting opportunities
- Withdrawal strategy suggestions
- Account location optimization

**Fee Analysis:**
- High expense ratio funds identified
- Lower-cost alternatives suggested
- Fee impact over time calculated
- Potential savings quantified

**Performance:**
- Underperforming investments identified
- Benchmark comparisons
- When to sell vs. hold
- Rebalancing timing

**How Often to Run:**
- Monthly for active management
- Quarterly for most investors
- After major market moves
- After adding significant funds
- Before major financial decisions

**Acting on Recommendations:**

**Priority Levels:**
- **High Priority:** Address immediately (major risk or opportunity)
- **Medium Priority:** Address within 3-6 months
- **Low Priority:** Nice to have, but optional

**Implementation Steps:**
1. Review all recommendations
2. Sort by priority
3. Start with high-priority items
4. Make changes gradually
5. Re-run analysis to see improvements

**Understanding AI Insights:**

**Gemini AI Uses:**
- Google's advanced Gemini AI model for deep analysis
- Modern Portfolio Theory principles
- Historical market data and trends
- Risk/return optimization algorithms
- Tax efficiency strategies
- Country-specific regulations and best practices
- Natural language processing for clear, understandable explanations
- Context-aware recommendations based on your complete financial picture

**Gemini AI Does NOT:**
- Make trades for you
- Predict market movements
- Guarantee returns
- Replace financial advisors
- Provide specific investment product recommendations

**Limitations:**
- Based on information you provide
- Historical data (past ≠ future)
- General recommendations
- Not personalized financial advice
- Should complement, not replace, professional advice

**Best Practices:**
- Keep portfolio data up-to-date
- Include ALL accounts for accurate analysis
- Don't blindly follow all recommendations
- Consider your personal circumstances
- Use alongside professional advice
- Re-run after making changes

**Educational Value:**
Even if you don't follow every recommendation, AI Analysis teaches you:
- How professional advisors think
- Portfolio construction principles
- Risk management strategies
- Tax optimization techniques
- Investment best practices

**Premium Value:**
- Analysis worth $500-1,000 from advisor
- Unlimited analyses included
- Always available 24/7
- No conflict of interest
- Fully transparent methodology

Upgrade to Premium to unlock AI Analysis and take your portfolio to the next level!`
        },
        {
          title: "Historical Backtesting",
          description: "Test strategies with historical data",
          content: `Test your portfolio against historical market data:

**What is Backtesting?**
See how your current portfolio would have performed during past market conditions, including recessions, bull markets, and crashes.

**How to Run Backtesting:**
1. Navigate to Insights > Historical Backtesting (Premium required)
2. Select time period:
   - Last 10 years
   - Last 20 years
   - Since 2000 (includes dot-com crash)
   - Since 1990
   - Custom date range
3. Click "Run Backtest"
4. Review results

**What You'll See:**

**Performance Chart:**
- How $10,000 would have grown
- Year-by-year returns
- Comparison to benchmarks (S&P 500, Total Market)
- Drawdown periods highlighted

**Key Metrics:**
- **Total Return:** Overall growth percentage
- **Annualized Return:** Average annual growth rate
- **Volatility:** How much portfolio fluctuated
- **Max Drawdown:** Worst peak-to-trough decline
- **Sharpe Ratio:** Risk-adjusted returns
- **Best Year:** Highest annual return
- **Worst Year:** Lowest annual return

**Notable Events:**
See how your portfolio would have handled:
- 2008 Financial Crisis (-37% market drop)
- 2020 COVID Crash (-34% in 1 month)
- 2000-2002 Dot-Com Crash (-49%)
- 2022 Bear Market (-25%)
- Various bull markets and recoveries

**Why Backtesting Matters:**

**Build Confidence:**
- See that recovery happens after crashes
- Understand normal volatility
- Reduce panic during downturns
- Stay invested long-term

**Stress Test Your Plan:**
- Can you handle the volatility?
- Would you have panicked and sold?
- Is your risk tolerance aligned?
- Do you need to adjust allocation?

**Compare Strategies:**
Test different allocations:
- 80/20 stocks/bonds vs 60/40
- With crypto vs without
- International exposure vs US-only
- Various sector allocations

**Limitations to Understand:**

**Past ≠ Future:**
- Historical returns don't guarantee future results
- Market conditions change
- New risks emerge
- Economic cycles vary

**Survivorship Bias:**
- Data includes companies that survived
- Doesn't fully account for bankruptcies
- Real results might be slightly lower

**No Trading Costs:**
- Backtests assume no fees or taxes
- Real returns would be slightly lower
- Still useful for comparison

**Perfect Information:**
- You're testing with hindsight
- Real investing means uncertainty
- Emotional decisions not factored in

**Using Backtest Results:**

**If Results Look Good:**
- Gives confidence to stay the course
- Validates your strategy
- Shows patience pays off
- Provides realistic expectations

**If Results Look Scary:**
- May need to reduce risk
- Consider more bonds/stable assets
- Ensure you can handle volatility
- Adjust before a real crash tests you

**Comparison Analysis:**

**Your Portfolio vs:**
- S&P 500 (US stocks)
- Total Stock Market
- 60/40 portfolio (60% stocks, 40% bonds)
- Target-date funds
- All-weather portfolio

**Questions to Answer:**
- Did you beat the market?
- Was the extra risk worth it?
- Could you have done better simply?
- Is your strategy too complicated?

**Real-World Application:**

**Scenario 1: Too Conservative**
Backtest shows you underperformed significantly:
- Missed major bull markets
- Returns barely beat inflation
- Too much in cash/bonds for your age
- **Action:** Increase stock allocation

**Scenario 2: Too Aggressive**
Backtest shows terrifying volatility:
- Lost 50%+ in 2008
- You would have panicked and sold
- Excessive risk for minimal extra return
- **Action:** Add bonds for stability

**Scenario 3: Well-Balanced**
Backtest shows reasonable returns with manageable volatility:
- Captured most of bull market gains
- Limited losses in bear markets
- You could have stayed invested
- **Action:** Stay the course!

**Premium Features:**
- Test multiple allocation strategies
- Custom time period selection
- Detailed year-by-year breakdown
- Export results to PDF
- Compare up to 5 portfolios side-by-side

**Psychological Benefits:**
- Seeing past recoveries reduces fear
- Understanding normal volatility
- Building conviction in your strategy
- Preventing emotional decisions
- Staying invested during downturns

**Best Practices:**
- Test pessimistic scenarios
- Include major crashes in time period
- Compare to simple index funds
- Focus on risk-adjusted returns, not just returns
- Consider your ability to handle volatility

**Educational Value:**
Backtesting teaches:
- Market history and cycles
- Impact of asset allocation
- Value of diversification
- Cost of market timing
- Benefits of staying invested

Use Historical Backtesting to build confidence in your investment strategy and prepare for future market volatility!`
        },
        {
          title: "Monte Carlo Simulations",
          description: "Understand probability-based projections",
          content: `See thousands of possible futures for your portfolio:

**What are Monte Carlo Simulations?**
Instead of showing one projection, Monte Carlo runs 1,000+ simulations with random market returns to show a range of possible outcomes.

**Why This Matters:**
- Markets don't grow smoothly
- Returns vary year to year
- Sequence of returns matters
- Simple projections can be misleading
- Need to see range of possibilities

**How It Works:**

**1. Input Your Data:**
- Current portfolio value
- Monthly contributions
- Time horizon (years to retirement)
- Expected return and volatility
- Withdrawal rate in retirement

**2. Run Simulations:**
- Algorithm runs 10,000+ scenarios
- Each with random (but realistic) returns
- Based on historical market behavior
- Accounts for volatility and sequence risk

**3. View Results:**
- **Success Rate:** % of simulations where money lasts
- **Probability Distribution:** Range of possible outcomes
- **Confidence Bands:** 10th, 50th, 90th percentile outcomes
- **Visual Chart:** Fan showing all scenarios

**Understanding Results:**

**Success Rate:**
- **90%+:** Very likely to succeed
- **75-90%:** Probably okay but risky
- **50-75%:** Significant chance of failure
- **<50%:** Plan needs major changes

**Percentile Outcomes:**

**90th Percentile (Best Case):**
- Top 10% of scenarios
- Market performs very well
- Portfolio grows significantly
- More than you need

**50th Percentile (Median):**
- Middle outcome
- Neither best nor worst
- Most likely scenario
- Reasonable planning number

**10th Percentile (Worst Case):**
- Bottom 10% of scenarios
- Market performs poorly
- Portfolio growth limited
- But still possible

**Interpreting the Fan Chart:**
- Wide fan = high uncertainty
- Narrow fan = more predictable
- Longer timeline = wider fan
- Shows possible portfolio values over time

**Practical Applications:**

**Retirement Planning:**
"Will my money last 30 years?"
- Input current savings and withdrawals
- See success rate
- Adjust if needed
- More reliable than simple projection

**Savings Goals:**
"Will I reach my $50,000 goal in 5 years?"
- Input monthly contributions
- See probability of success
- Adjust savings rate if needed

**Early Retirement (FIRE):**
"Can I retire at 45?"
- Test aggressive savings plans
- Account for long retirement horizon
- See probability of success
- Make informed decision

**Adjusting Your Plan:**

**If Success Rate < 75%:**
1. **Save More:** Increase monthly contributions
2. **Spend Less:** Reduce retirement withdrawals
3. **Work Longer:** Delay retirement 2-3 years
4. **Higher Returns:** Take slightly more risk (carefully)
5. **Combination:** Multiple small adjustments

**Impact Analysis:**
See how changes affect success rate:
- Save $200 more per month: 65% → 78%
- Work 2 more years: 65% → 82%
- Reduce spending 10%: 65% → 75%
- Combination of above: 65% → 92%

**Sequence of Returns Risk:**

**Why It Matters:**
- Returns in first years of retirement critical
- Large losses early = devastating
- Large gains early = protective
- Simple averages miss this
- Monte Carlo captures this risk

**Example:**
Same average return, different sequences:
- **Good Sequence:** +20%, +10%, -5%, +8% = success
- **Bad Sequence:** -20%, -10%, +15%, +28% = failure
- Average return identical in both!

**Protecting Against Sequence Risk:**
- Build cash buffer (2-3 years expenses)
- Bonds for stability in early retirement
- Flexible spending (reduce in bad years)
- Part-time work option
- Delay Social Security for higher benefit

**Comparing Scenarios:**

Run multiple simulations:
1. **Baseline:** Current plan
2. **Optimistic:** Higher returns assumed
3. **Pessimistic:** Lower returns assumed
4. **Conservative Allocation:** More bonds
5. **Aggressive Allocation:** More stocks

See which gives acceptable success rate with risk you're comfortable with.

**Limitations:**

**Assumptions:**
- Based on historical volatility
- Assumes future like past
- Doesn't predict black swans
- Normal distribution assumed

**Not Guarantees:**
- High success rate ≠ guarantee
- Low success rate ≠ failure
- Many factors not modeled
- Use as guide, not crystal ball

**Premium Features:**
- Run unlimited simulations
- Custom volatility assumptions
- Adjust retirement spending rules
- Inflation modeling
- Tax impact considerations
- Save and compare scenarios
- Export detailed reports

**Real-World Example:**

**Scenario:** John, 55, wants to retire at 65
- Current savings: $500,000
- Monthly saving: $2,000
- Desired retirement income: $60,000/year
- Expected return: 7%, Volatility: 15%

**Monte Carlo Results:**
- Success rate: 68%
- Too risky!

**Adjustments:**
- Work to 67 instead: Success rate 85%
- OR save $2,500/month: Success rate 82%
- OR reduce spending to $55k/year: Success rate 80%

John chooses to work to 67 and is confident in his plan.

**Best Practices:**
- Run simulations regularly (annually)
- Update as circumstances change
- Use conservative assumptions
- Don't chase 100% success (impossible)
- 85-90% is good target
- Build flexibility into plan
- Have backup options

**Peace of Mind:**
Monte Carlo simulations provide:
- Realistic expectations
- Understanding of risks
- Confidence in achievable plans
- Early warning of problems
- Data-driven decision making
- Reduced anxiety about future

Use Monte Carlo Simulations to stress-test your retirement plan and make confident financial decisions!`
        },
        {
          title: "Exporting Reports",
          description: "Download PDF and CSV reports",
          content: `Export your financial data and reports with Premium:

**What You Can Export:**

**1. CSV Data Export (All Users):**
- Investment list with all details
- Download and open in Excel/Sheets
- Backup your data
- Analyze elsewhere
- Share with advisor

**How to Export CSV:**
1. Go to Wealth > Assets
2. Click "Export CSV"
3. File downloads automatically
4. Open in Excel, Google Sheets, etc.

**CSV Includes:**
- Investment name and ticker
- Amount invested
- Expected return and risk
- Category and account
- Purchase date
- All custom fields

**2. Portfolio Report (Premium):**
- Comprehensive PDF report
- Professional formatting
- Ready to share with advisor
- Print or save digitally

**Portfolio Report Contents:**
- Portfolio summary and allocation
- Asset breakdown by category
- Account-level details
- Performance metrics
- Risk assessment
- Holdings list with values
- Net worth summary
- Goals progress
- Liabilities overview

**3. Net Worth Report (Premium):**
- Complete financial snapshot
- Assets and liabilities
- Net worth trend over time
- Month-over-month changes
- Financial health score
- Detailed breakdowns

**4. Goals Progress Report (Premium):**
- All goals with status
- Progress bars and percentages
- On-track analysis
- Required vs. actual contributions
- Timeline projections
- Recommendations

**5. AI Analysis Report (Premium):**
- Full AI analysis results
- Risk assessment
- Diversification analysis
- Recommendations
- Scenario projections
- Action items

**6. Retirement Analysis (Premium):**
- Current projections
- Required savings calculations
- Gap analysis
- Multiple scenarios
- Monte Carlo results (if run)
- Historical backtesting (if run)

**How to Export PDF Reports:**
1. Navigate to the section you want (Portfolio, Net Worth, etc.)
2. Click "Export to PDF" button
3. Choose report type
4. Wait for generation (5-15 seconds)
5. PDF downloads automatically
6. Save or print as needed

**Report Customization (Premium):**
- Choose date range
- Include/exclude sections
- Select specific accounts
- Filter by category
- Add custom notes
- Professional letterhead option

**Use Cases:**

**For Financial Advisors:**
- Share complete portfolio view
- Prepare for meetings
- Get professional recommendations
- Track advisor's impact over time

**For Tax Professionals:**
- Capital gains/losses documentation
- Investment income summary
- Account location optimization
- Cost basis tracking

**For Record Keeping:**
- Annual portfolio snapshots
- Progress documentation
- Estate planning
- Insurance needs analysis

**For Spouse/Family:**
- Transparency in finances
- Joint planning
- Emergency access to information
- Legacy planning

**For Loan Applications:**
- Asset documentation
- Net worth proof
- Income potential
- Financial stability demonstration

**Best Practices:**

**Regular Exports:**
- Monthly CSV backup (safety)
- Quarterly portfolio report (progress tracking)
- Annual comprehensive report (tax time)
- Before major decisions (baseline)

**Organization:**
- Create folder structure
- Name files with dates
- Keep historical reports
- Easy to compare over time

**Security:**
- Store securely (encrypted drive)
- Password protect PDFs if emailing
- Don't share publicly
- Be cautious with sensitive data

**Sharing with Advisors:**
- Export full portfolio report
- Include recent AI analysis
- Add any questions as notes
- Send securely (encrypted email)

**PDF Report Features:**

**Professional Quality:**
- Clean, modern design
- Charts and graphs
- Color-coded indicators
- Easy to read formatting
- Printable at high quality

**Interactive Elements:**
- Clickable table of contents
- Hyperlinked sections
- Bookmarks for navigation
- Searchable text

**Branding:**
- PlanLens professional formatting
- Your name and date
- Disclaimer and disclosures
- "Generated by PlanLens" watermark

**Data Privacy:**

**What We Don't Include:**
- No account numbers
- No passwords
- No personally identifiable info beyond name
- No social security numbers
- Your privacy protected

**You Control:**
- What gets exported
- Who you share with
- Where files are stored
- When to delete old reports

**Technical Details:**

**CSV Format:**
- Standard comma-separated values
- UTF-8 encoding
- Compatible with all spreadsheet software
- Easy to import elsewhere

**PDF Format:**
- PDF/A standard
- Printable and digital
- Cross-platform compatible
- Searchable text

**File Sizes:**
- CSV: Usually < 100KB
- PDF reports: 500KB - 2MB
- Downloads are fast
- Easy to email or store

**Troubleshooting:**

**Download Not Working:**
- Check popup blocker settings
- Try different browser
- Clear browser cache
- Contact support if persists

**PDF Won't Open:**
- Update Adobe Reader
- Try different PDF viewer
- Re-download file
- Check file wasn't corrupted

**CSV Formatting Issues:**
- Open in spreadsheet app, not text editor
- Check delimiter settings (comma)
- Ensure UTF-8 encoding
- Re-export if needed

**Premium Value:**
- Professional reports worth $50-100 each
- Unlimited exports included
- Always up-to-date data
- Save thousands on advisor reports

Upgrade to Premium to unlock comprehensive reporting and professional-grade exports!`
        }
      ]
    }
  ];

  const learnTopics = [
    {
      category: "Investment Basics",
      icon: "📚",
      topics: [
        { title: "What are Stocks?", description: "Understanding equity investments and ownership in companies" },
        { title: "Bonds Explained", description: "Fixed-income securities and how they work" },
        { title: "Real Estate Investing", description: "REITs and property investments" },
        { title: "Asset Allocation", description: "Diversifying your portfolio across asset classes" }
      ]
    },
    {
      category: "Retirement Accounts",
      icon: "🏦",
      topics: [
        { title: "401(k) Plans (US)", description: "Employer-sponsored retirement savings" },
        { title: "IRA vs Roth IRA (US)", description: "Traditional and Roth retirement accounts" },
        { title: "ISA Accounts (UK)", description: "Individual Savings Accounts explained" },
        { title: "SIPP & Pensions (UK)", description: "Self-Invested Personal Pensions" },
        { title: "Contribution Limits", description: "Annual limits and tax advantages" }
      ]
    },
    {
      category: "Financial Planning",
      icon: "🎯",
      topics: [
        { title: "Setting SMART Goals", description: "Specific, Measurable, Achievable, Relevant, Time-bound goals" },
        { title: "Emergency Funds", description: "Building financial safety nets" },
        { title: "Debt Management", description: "Strategies for paying down debt" },
        { title: "Net Worth Calculation", description: "Assets minus liabilities explained" },
        { title: "Cash Flow Management", description: "Income, expenses, and savings rate" }
      ]
    },
    {
      category: "Risk & Returns",
      icon: "📊",
      topics: [
        { title: "Understanding Risk", description: "Volatility and risk tolerance" },
        { title: "Expected Returns", description: "Historical returns and future projections" },
        { title: "Sharpe Ratio", description: "Risk-adjusted return measurements" },
        { title: "Diversification", description: "Reducing risk through portfolio diversity" },
        { title: "Rebalancing", description: "Maintaining your target allocation" }
      ]
    },
    {
      category: "Tax Optimization",
      icon: "💰",
      topics: [
        { title: "Tax-Advantaged Accounts", description: "Reducing your tax burden" },
        { title: "Capital Gains Tax (US)", description: "Short-term vs long-term gains" },
        { title: "UK Tax Rules", description: "CGT, ISA benefits, and allowances" },
        { title: "Tax-Loss Harvesting", description: "Offsetting gains with losses" },
        { title: "Withdrawal Strategies", description: "Tax-efficient retirement withdrawals" }
      ]
    }
  ];

  const faqs = [
    {
      category: "General",
      questions: [
        {
          q: "Is PlanLens really free?",
          a: "Yes! Our free plan includes portfolio tracking, net worth dashboard, financial goals, liabilities management, and basic retirement planning. Premium features like Gemini AI analysis and advanced simulations require a paid subscription."
        },
        {
          q: "How is my data secured?",
          a: "We use bank-level encryption and store all data securely in Firebase with industry-standard security practices. Your data is private and never shared with third parties."
        },
        {
          q: "Can I use PlanLens on my phone?",
          a: "Yes! PlanLens is fully responsive and works great on mobile devices, tablets, and desktops. Your data syncs automatically across all devices."
        },
        {
          q: "Do you support countries other than US and UK?",
          a: "Currently, PlanLens is optimized for US and UK users with country-specific account types and tax rules. We plan to add more countries in the future."
        }
      ]
    },
    {
      category: "Features",
      questions: [
        {
          q: "What's the difference between free and premium?",
          a: "Free includes all basic financial planning tools. Premium adds Gemini AI-powered analysis, historical backtesting, Monte Carlo simulations, enhanced retirement planning with scenario analysis, and priority support."
        },
        {
          q: "How does the net worth calculator work?",
          a: "We calculate your net worth by adding all your assets (investments, cash) and subtracting all liabilities (mortgages, loans, credit cards). You get a real-time financial health score and detailed breakdowns."
        },
        {
          q: "How do financial goals work?",
          a: "Set goals with target amounts and dates. PlanLens calculates required monthly savings, projects future values with compound interest, and warns you if you're off track. Track progress with visual indicators."
        },
        {
          q: "What is Gemini AI Analysis?",
          a: "Our Gemini AI (powered by Google's advanced AI) analyzes your entire portfolio across all accounts and provides personalized recommendations, comprehensive risk assessments, diversification scores, and optimization strategies tailored to your situation with natural language explanations."
        }
      ]
    },
    {
      category: "Account & Billing",
      questions: [
        {
          q: "How do I upgrade to premium?",
          a: "Click the 'Upgrade' button in the app header or any premium feature. You'll get a 14-day free trial, then it's $5.99/month (US) or £4.99/month (UK). Cancel anytime."
        },
        {
          q: "Can I cancel my subscription?",
          a: "Yes, cancel anytime with one click. Your data remains accessible even after cancellation, but premium features will be locked."
        },
        {
          q: "Do you offer refunds?",
          a: "Yes, we offer a 30-day money-back guarantee on all premium subscriptions. Contact support for a full refund within 30 days."
        },
        {
          q: "How do I change my country setting?",
          a: "Your country is set during signup and determines currency and account types. Contact support if you need to change it."
        }
      ]
    },
    {
      category: "Data & Privacy",
      questions: [
        {
          q: "Do you connect to my bank accounts?",
          a: "No, PlanLens does not connect directly to your bank accounts. You manually enter your investment data, which gives you complete control and privacy."
        },
        {
          q: "Can I export my data?",
          a: "Yes! Export your investments to CSV anytime. Premium users can also export full portfolio reports to PDF."
        },
        {
          q: "What happens if I delete my account?",
          a: "All your data will be permanently deleted from our servers within 30 days. You can export your data before deletion."
        },
        {
          q: "Do you sell my data?",
          a: "Never. We don't sell, share, or monetize your personal financial data in any way. Your privacy is our top priority."
        }
      ]
    },
    {
      category: "Technical",
      questions: [
        {
          q: "Which browsers are supported?",
          a: "PlanLens works on all modern browsers including Chrome, Firefox, Safari, and Edge. We recommend using the latest version for the best experience."
        },
        {
          q: "Can I import data from other apps?",
          a: "Yes! Use our CSV import feature to bulk import investments. We provide a template to make it easy."
        },
        {
          q: "How often does data sync?",
          a: "Data syncs automatically in real-time whenever you make changes. All your devices stay up-to-date instantly."
        }
      ]
    }
  ];

  const filteredFaqs = searchQuery
    ? faqs.map(category => ({
        ...category,
        questions: category.questions.filter(
          q =>
            q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqs;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-2xl z-10">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h2 className="text-2xl font-bold mb-1">Resources & Help Center</h2>
              <p className="text-sm text-indigo-100">Everything you need to succeed with PlanLens</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Section Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveSection('help')}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === 'help'
                  ? 'bg-white text-indigo-600'
                  : 'bg-indigo-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              <div className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                Help Articles
              </div>
            </button>
            <button
              onClick={() => setActiveSection('learn')}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === 'learn'
                  ? 'bg-white text-indigo-600'
                  : 'bg-indigo-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Learn
              </div>
            </button>
            <button
              onClick={() => setActiveSection('faq')}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === 'faq'
                  ? 'bg-white text-indigo-600'
                  : 'bg-indigo-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                FAQ
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Help Articles Section */}
          {activeSection === 'help' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">How Can We Help?</h3>
                <p className="text-gray-600">Browse articles by category or search for specific topics</p>
              </div>

              {helpArticles.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">{category.category}</h4>
                  <div className="space-y-3">
                    {category.articles.map((article, articleIndex) => {
                      const articleId = `help-${categoryIndex}-${articleIndex}`;
                      const isExpanded = expandedArticle === articleId;

                      return (
                        <div key={articleIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => setExpandedArticle(isExpanded ? null : articleId)}
                            className="w-full flex items-start gap-3 p-4 bg-gray-50 hover:bg-gray-100 text-left transition-colors group"
                          >
                            <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h5 className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                                {article.title}
                              </h5>
                              <p className="text-sm text-gray-600 mt-1">{article.description}</p>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          {isExpanded && (
                            <div className="p-6 bg-white border-t border-gray-200">
                              <div className="prose prose-sm max-w-none">
                                {article.content.split('\n').map((line, idx) => {
                                  if (line.startsWith('**') && line.endsWith('**')) {
                                    return <h4 key={idx} className="font-bold text-gray-900 mt-4 mb-2">{line.replace(/\*\*/g, '')}</h4>;
                                  } else if (line.startsWith('- ')) {
                                    return <li key={idx} className="ml-4 text-gray-700">{line.substring(2)}</li>;
                                  } else if (line.trim() === '') {
                                    return <br key={idx} />;
                                  } else {
                                    return <p key={idx} className="text-gray-700 mb-2">{line}</p>;
                                  }
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Learn Section */}
          {activeSection === 'learn' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Financial Education</h3>
                <p className="text-gray-600">Build your financial knowledge with our comprehensive guides</p>
              </div>

              {learnTopics.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{category.icon}</span>
                    <h4 className="text-xl font-bold text-gray-900">{category.category}</h4>
                  </div>
                  <div className="space-y-3">
                    {category.topics.map((topic, topicIndex) => {
                      const topicId = `learn-${categoryIndex}-${topicIndex}`;
                      const isExpanded = expandedTopic === topicId;

                      return (
                        <div key={topicIndex} className="border border-purple-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => setExpandedTopic(isExpanded ? null : topicId)}
                            className="w-full flex items-start gap-3 p-4 bg-gradient-to-br from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 text-left transition-colors group"
                          >
                            <BookOpen className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h5 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                                {topic.title}
                              </h5>
                              <p className="text-sm text-gray-600 mt-1">{topic.description}</p>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          {isExpanded && (
                            <div className="p-6 bg-white border-t border-purple-200">
                              <div className="prose prose-sm max-w-none">
                                <p className="text-gray-700">{topic.description}</p>
                                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                  <p className="text-sm text-blue-800">
                                    📚 <strong>Want to learn more?</strong> This is an overview topic. For detailed guides, tutorials, and expert insights, explore the full Help Articles section or contact our support team for personalized guidance.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white text-center">
                <h4 className="text-xl font-bold mb-2">Want to Learn More?</h4>
                <p className="text-blue-100 mb-4">
                  Access our full knowledge base with detailed guides, expert tips, and comprehensive articles
                </p>
                <button
                  onClick={() => setActiveSection('help')}
                  className="px-6 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  Visit Knowledge Base
                </button>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {activeSection === 'faq' && (
            <div>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Frequently Asked Questions</h3>
                <p className="text-gray-600 mb-4">Find quick answers to common questions</p>

                {/* Search */}
                <div className="max-w-md mx-auto relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search FAQs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-6">
                {filteredFaqs.map((category, categoryIndex) => (
                  <div key={categoryIndex}>
                    <h4 className="text-xl font-bold text-gray-900 mb-4">{category.category}</h4>
                    <div className="space-y-3">
                      {category.questions.map((faq, faqIndex) => (
                        <details
                          key={faqIndex}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <summary className="font-semibold text-gray-900 cursor-pointer flex items-start gap-2">
                            <HelpCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                            <span>{faq.q}</span>
                          </summary>
                          <p className="text-gray-600 mt-3 ml-7 text-sm">{faq.a}</p>
                        </details>
                      ))}
                    </div>
                  </div>
                ))}

                {filteredFaqs.length === 0 && (
                  <div className="text-center py-12">
                    <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No results found for "{searchQuery}"</p>
                    <p className="text-sm text-gray-500 mt-2">Try a different search term</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 rounded-b-2xl">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              For additional support, please log in to your account and access the Support section from the main menu.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesPage;
